// Demo service for Tasks - provides mock data when API is unavailable

import type { Task, TaskFilters, TaskStatistics, TaskStatus, TaskPriority, TaskType } from '@/types/task';
import { demoTasks, demoComments, demoAttachments, demoActivity } from '@/data/demo-tasks';

// Use the centralized demo tasks data
const mockTasks: Task[] = demoTasks;

// Legacy mock tasks (kept for reference, but not used)
const _legacyMockTasks: Task[] = [
  {
    id: 'task-1',
    title: 'Review asset depreciation schedule',
    description: 'Annual review of depreciation schedules for all IT equipment',
    type: 'General Task',
    priority: 'High',
    status: 'In Progress',
    assignee: {
      id: 'user-1',
      name: 'John Smith',
      email: 'john.smith@example.com',
      avatar: undefined,
      type: 'user',
    },
    assignedBy: 'Jane Doe',
    assignedAt: '2024-01-15T10:00:00Z',
    createdAt: '2024-01-10T09:00:00Z',
    updatedAt: '2024-01-20T14:30:00Z',
    dueDate: '2024-02-01T17:00:00Z',
    startDate: '2024-01-15T09:00:00Z',
    links: [
      { id: 'link-1', type: 'asset', entityId: 'asset-1', entityName: 'Dell Laptop XPS 15' },
    ],
    tags: ['depreciation', 'annual-review', 'IT'],
    checklists: [
      { id: 'cl-1', title: 'Review IT equipment', completed: true, mandatory: true, createdAt: '2024-01-10T09:00:00Z', completedAt: '2024-01-18T11:00:00Z', completedBy: 'John Smith' },
      { id: 'cl-2', title: 'Review office furniture', completed: false, mandatory: true, createdAt: '2024-01-10T09:00:00Z' },
      { id: 'cl-3', title: 'Update depreciation rates', completed: false, mandatory: false, createdAt: '2024-01-10T09:00:00Z' },
    ],
    subtasks: [
      { id: 'st-1', title: 'Gather asset list', status: 'Closed', assigneeId: 'user-1', assigneeName: 'John Smith' },
      { id: 'st-2', title: 'Calculate new values', status: 'In Progress', assigneeId: 'user-1', assigneeName: 'John Smith' },
    ],
    createdBy: 'Jane Doe',
    updatedBy: 'John Smith',
    commentsCount: 3,
    attachmentsCount: 2,
    lastActivityAt: '2024-01-20T14:30:00Z',
    lastActivityBy: 'John Smith',
    linkedAssets: [],
  },
  {
    id: 'task-2',
    title: 'Asset verification for Building A',
    description: 'Physical verification of all assets in Building A',
    type: 'Verification',
    priority: 'Urgent',
    status: 'New',
    assignee: {
      id: 'user-2',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@example.com',
      type: 'user',
    },
    assignedBy: 'Admin',
    assignedAt: '2024-01-22T08:00:00Z',
    createdAt: '2024-01-22T08:00:00Z',
    updatedAt: '2024-01-22T08:00:00Z',
    dueDate: '2024-01-25T17:00:00Z',
    links: [
      { id: 'link-2', type: 'location', entityId: 'loc-1', entityName: 'Building A' },
    ],
    tags: ['verification', 'urgent', 'building-a'],
    checklists: [],
    subtasks: [],
    createdBy: 'Admin',
    commentsCount: 0,
    attachmentsCount: 0,
    lastActivityAt: '2024-01-22T08:00:00Z',
    lastActivityBy: 'Admin',
    linkedAssets: [],
  },
  {
    id: 'task-3',
    title: 'Update insurance documentation',
    description: 'Renew and update insurance documents for high-value assets',
    type: 'Documentation Update',
    priority: 'Medium',
    status: 'Blocked',
    assignee: {
      id: 'user-3',
      name: 'Mike Wilson',
      email: 'mike.wilson@example.com',
      type: 'user',
    },
    assignedBy: 'Jane Doe',
    assignedAt: '2024-01-18T10:00:00Z',
    createdAt: '2024-01-18T10:00:00Z',
    updatedAt: '2024-01-21T16:00:00Z',
    dueDate: '2024-01-30T17:00:00Z',
    links: [],
    tags: ['insurance', 'documentation'],
    checklists: [
      { id: 'cl-4', title: 'Contact insurance provider', completed: true, mandatory: true, createdAt: '2024-01-18T10:00:00Z', completedAt: '2024-01-19T14:00:00Z', completedBy: 'Mike Wilson' },
      { id: 'cl-5', title: 'Receive updated quotes', completed: false, mandatory: true, createdAt: '2024-01-18T10:00:00Z' },
    ],
    subtasks: [],
    createdBy: 'Jane Doe',
    updatedBy: 'Mike Wilson',
    commentsCount: 5,
    attachmentsCount: 1,
    lastActivityAt: '2024-01-21T16:00:00Z',
    lastActivityBy: 'Mike Wilson',
    linkedAssets: [],
  },
  {
    id: 'task-4',
    title: 'Quarterly maintenance inspection',
    description: 'Perform Q1 maintenance inspection for all machinery',
    type: 'Inspection',
    priority: 'High',
    status: 'Resolved',
    assignee: {
      id: 'user-4',
      name: 'Emily Brown',
      email: 'emily.brown@example.com',
      type: 'user',
    },
    assignedBy: 'Admin',
    assignedAt: '2024-01-05T09:00:00Z',
    createdAt: '2024-01-05T09:00:00Z',
    updatedAt: '2024-01-19T17:00:00Z',
    dueDate: '2024-01-20T17:00:00Z',
    resolvedAt: '2024-01-19T17:00:00Z',
    links: [
      { id: 'link-3', type: 'audit', entityId: 'audit-1', entityName: 'Q1 2024 Audit' },
    ],
    tags: ['maintenance', 'quarterly', 'machinery'],
    checklists: [
      { id: 'cl-6', title: 'Inspect production line A', completed: true, mandatory: true, createdAt: '2024-01-05T09:00:00Z', completedAt: '2024-01-15T11:00:00Z', completedBy: 'Emily Brown' },
      { id: 'cl-7', title: 'Inspect production line B', completed: true, mandatory: true, createdAt: '2024-01-05T09:00:00Z', completedAt: '2024-01-17T14:00:00Z', completedBy: 'Emily Brown' },
      { id: 'cl-8', title: 'Document findings', completed: true, mandatory: true, createdAt: '2024-01-05T09:00:00Z', completedAt: '2024-01-19T16:00:00Z', completedBy: 'Emily Brown' },
    ],
    subtasks: [],
    createdBy: 'Admin',
    updatedBy: 'Emily Brown',
    commentsCount: 8,
    attachmentsCount: 4,
    lastActivityAt: '2024-01-19T17:00:00Z',
    lastActivityBy: 'Emily Brown',
    linkedAssets: [],
  },
  {
    id: 'task-5',
    title: 'Data collection for annual report',
    description: 'Collect asset data for the annual financial report',
    type: 'Data Collection',
    priority: 'Low',
    status: 'Closed',
    assignee: {
      id: 'user-1',
      name: 'John Smith',
      email: 'john.smith@example.com',
      type: 'user',
    },
    assignedBy: 'Finance Team',
    assignedAt: '2024-01-02T09:00:00Z',
    createdAt: '2024-01-02T09:00:00Z',
    updatedAt: '2024-01-12T17:00:00Z',
    dueDate: '2024-01-15T17:00:00Z',
    resolvedAt: '2024-01-12T15:00:00Z',
    closedAt: '2024-01-12T17:00:00Z',
    links: [],
    tags: ['annual-report', 'data', 'finance'],
    checklists: [],
    subtasks: [],
    createdBy: 'Finance Team',
    updatedBy: 'John Smith',
    commentsCount: 2,
    attachmentsCount: 1,
    lastActivityAt: '2024-01-12T17:00:00Z',
    lastActivityBy: 'John Smith',
    linkedAssets: [],
  },
];

/**
 * Apply search filter to tasks
 */
function applySearchFilter(tasks: Task[], filters: TaskFilters): Task[] {
  if (!filters.search) return tasks;
  const search = filters.search.toLowerCase();
  return tasks.filter(t =>
    t.title.toLowerCase().includes(search) ||
    t.description?.toLowerCase().includes(search) ||
    t.tags.some(tag => tag.toLowerCase().includes(search))
  );
}

/**
 * Apply status filters to tasks
 */
function applyStatusFilters(tasks: Task[], filters: TaskFilters): Task[] {
  let result = tasks;
  if (filters.status) {
    result = result.filter(t => t.status === filters.status);
  }
  if (filters.statuses && filters.statuses.length > 0) {
    result = result.filter(t => filters.statuses!.includes(t.status));
  }
  return result;
}

/**
 * Apply type filters to tasks
 */
function applyTypeFilters(tasks: Task[], filters: TaskFilters): Task[] {
  let result = tasks;
  if (filters.type) {
    result = result.filter(t => t.type === filters.type);
  }
  if (filters.types && filters.types.length > 0) {
    result = result.filter(t => filters.types!.includes(t.type));
  }
  return result;
}

/**
 * Apply priority filters to tasks
 */
function applyPriorityFilters(tasks: Task[], filters: TaskFilters): Task[] {
  let result = tasks;
  if (filters.priority) {
    result = result.filter(t => t.priority === filters.priority);
  }
  if (filters.priorities && filters.priorities.length > 0) {
    result = result.filter(t => filters.priorities!.includes(t.priority));
  }
  return result;
}

/**
 * Apply assignee filter to tasks
 */
function applyAssigneeFilter(tasks: Task[], filters: TaskFilters): Task[] {
  if (!filters.assigneeIds || filters.assigneeIds.length === 0) return tasks;
  return tasks.filter(t => t.assignee && filters.assigneeIds!.includes(t.assignee.id));
}

/**
 * Apply date filters (overdue, dueToday, dueThisWeek) to tasks
 */
function applyDateFilters(tasks: Task[], filters: TaskFilters): Task[] {
  if (!filters.isOverdue && !filters.dueToday && !filters.dueThisWeek) return tasks;

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

  let result = tasks;

  if (filters.isOverdue) {
    result = result.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = new Date(t.dueDate);
      return dueDate < now && !['Resolved', 'Closed'].includes(t.status);
    });
  }

  if (filters.dueToday) {
    result = result.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= today && dueDate < new Date(today.getTime() + 24 * 60 * 60 * 1000);
    });
  }

  if (filters.dueThisWeek) {
    result = result.filter(t => {
      if (!t.dueDate) return false;
      const dueDate = new Date(t.dueDate);
      return dueDate >= today && dueDate <= endOfWeek;
    });
  }

  return result;
}

/**
 * Apply tags filter to tasks
 */
function applyTagsFilter(tasks: Task[], filters: TaskFilters): Task[] {
  if (!filters.tags || filters.tags.length === 0) return tasks;
  return tasks.filter(t => filters.tags!.some(tag => t.tags.includes(tag)));
}

/**
 * Apply link filters (assetId, locationId, auditId, exceptionId) to tasks
 */
function applyLinkFilters(tasks: Task[], filters: TaskFilters): Task[] {
  let result = tasks;
  if (filters.assetId) {
    result = result.filter(t => t.links.some(l => l.type === 'asset' && l.entityId === filters.assetId));
  }
  if (filters.locationId) {
    result = result.filter(t => t.links.some(l => l.type === 'location' && l.entityId === filters.locationId));
  }
  if (filters.auditId) {
    result = result.filter(t => t.links.some(l => l.type === 'audit' && l.entityId === filters.auditId));
  }
  if (filters.exceptionId) {
    result = result.filter(t => t.links.some(l => l.type === 'exception' && l.entityId === filters.exceptionId));
  }
  return result;
}

/**
 * Apply sorting to tasks
 */
function applySorting(tasks: Task[], filters: TaskFilters): Task[] {
  if (!filters.sortBy) return tasks;

  const sortBy = filters.sortBy as keyof Task;
  const order = filters.order ?? 'asc';
  const sorted = [...tasks];
  sorted.sort((a, b) => {
    const aVal = a[sortBy];
    const bVal = b[sortBy];
    if (aVal === undefined || bVal === undefined) return 0;
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
}

/**
 * Filter tasks based on provided filters
 */
function filterTasks(tasks: Task[], filters?: TaskFilters): Task[] {
  if (!filters) return tasks;

  let result = [...tasks];
  result = applySearchFilter(result, filters);
  result = applyStatusFilters(result, filters);
  result = applyTypeFilters(result, filters);
  result = applyPriorityFilters(result, filters);
  result = applyAssigneeFilter(result, filters);
  result = applyDateFilters(result, filters);
  result = applyTagsFilter(result, filters);
  result = applyLinkFilters(result, filters);
  result = applySorting(result, filters);

  return result;
}

/**
 * Get tasks with filtering and pagination (demo mode)
 */
export function getTasksDemo(filters?: TaskFilters): { data: Task[]; total: number; page: number; pageSize: number; totalPages: number } {
  const filtered = filterTasks(mockTasks, filters);
  
  const page = filters?.page ?? 1;
  const pageSize = filters?.pageSize ?? 20;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    data: filtered.slice(start, end),
    total: filtered.length,
    page,
    pageSize,
    totalPages: Math.ceil(filtered.length / pageSize),
  };
}

/**
 * Get a single task by ID (demo mode)
 */
export function getTaskDemo(id: string): Task {
  const task = mockTasks.find(t => t.id === id);
  if (!task) {
    throw new Error(`Task with ID ${id} not found`);
  }
  return task;
}

/**
 * Get task comments (demo mode)
 */
export function getTaskCommentsDemo(taskId: string): any[] {
  return demoComments.filter((c: any) => c.taskId === taskId);
}

/**
 * Get task attachments (demo mode)
 */
export function getTaskAttachmentsDemo(taskId: string): any[] {
  return demoAttachments.filter((a: any) => a.taskId === taskId);
}

/**
 * Get task activity (demo mode)
 */
export function getTaskActivityDemo(taskId: string): any[] {
  const activities = demoActivity.filter((a: any) => a.taskId === taskId);
  console.log(`getTaskActivityDemo for ${taskId}:`, activities);
  
  // If no activity found, return empty array (not default activity)
  // This allows the UI to show "No activity yet" message
  return activities;
}

/**
 * Get task statistics (demo mode)
 */
export function getTaskStatisticsDemo(): TaskStatistics {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + (7 - today.getDay()));

  const byStatus: Record<TaskStatus, number> = {
    'New': 0,
    'In Progress': 0,
    'Blocked': 0,
    'Resolved': 0,
    'Closed': 0,
  };

  const byType: Record<TaskType, number> = {
    'General Task': 0,
    'Inspection': 0,
    'Maintenance Request': 0,
    'Documentation Update': 0,
    'Verification': 0,
    'Follow-up Action': 0,
    'Data Collection': 0,
    'Task': 0,
    'Bug': 0,
    'Feature': 0,
    'Maintenance': 0,
  };

  const byPriority: Record<TaskPriority, number> = {
    'Urgent': 0,
    'High': 0,
    'Medium': 0,
    'Low': 0,
  };

  let overdue = 0;
  let dueToday = 0;
  let dueThisWeek = 0;

  for (const task of mockTasks) {
    byStatus[task.status]++;
    byType[task.type]++;
    byPriority[task.priority]++;

    if (task.dueDate) {
      const dueDate = new Date(task.dueDate);
      if (dueDate < now && !['Resolved', 'Closed'].includes(task.status)) {
        overdue++;
      }
      if (dueDate >= today && dueDate < new Date(today.getTime() + 24 * 60 * 60 * 1000)) {
        dueToday++;
      }
      if (dueDate >= today && dueDate <= endOfWeek) {
        dueThisWeek++;
      }
    }
  }

  return {
    totalTasks: mockTasks.length,
    byStatus,
    byType,
    byPriority,
    overdue,
    dueToday,
    dueThisWeek,
  };
}

/**
 * Create a new task (demo mode)
 */
export function createTaskDemo(taskData: any): Task {
  const now = new Date().toISOString();
  const newTask: Task = {
    id: `TASK-${mockTasks.length + 1}`,
    title: taskData.title,
    description: taskData.description ?? '',
    type: taskData.type,
    priority: taskData.priority,
    status: 'New',
    assignee: taskData.assigneeId ? {
      id: taskData.assigneeId,
      name: 'Demo User',
      email: 'demo@example.com',
      type: 'user',
    } : undefined,
    assignedBy: 'Current User',
    assignedAt: now,
    createdAt: now,
    updatedAt: now,
    dueDate: taskData.dueDate,
    startDate: now,
    links: [],
    linkedAssets: [],
    tags: taskData.tags ?? [],
    checklists: [],
    subtasks: [],
    createdBy: 'Current User',
    updatedBy: 'Current User',
    commentsCount: 0,
    attachmentsCount: 0,
    lastActivityAt: now,
    lastActivityBy: 'Current User',
  };

  // Add to the beginning of the array so it appears first
  mockTasks.unshift(newTask);
  
  return newTask;
}

/**
 * Update an existing task (demo mode)
 */
export function updateTaskDemo(id: string, taskData: any): Task {
  const taskIndex = mockTasks.findIndex(t => t.id === id);
  if (taskIndex === -1) {
    throw new Error(`Task with ID ${id} not found`);
  }

  const now = new Date().toISOString();
  const oldTask = mockTasks[taskIndex];
  
  // Track changes for activity log
  const changes: Array<{ type: string; description: string; oldValue?: string; newValue?: string }> = [];
  
  // Check for status change
  if (taskData.status && taskData.status !== oldTask.status) {
    changes.push({
      type: 'status-change',
      description: 'changed status',
      oldValue: oldTask.status,
      newValue: taskData.status,
    });
  }
  
  // Check for assignee change
  if (taskData.assignedToUserId && taskData.assignedToUserId !== oldTask.assignee?.id) {
    changes.push({
      type: 'assignee-change',
      description: 'changed assignee',
            oldValue: oldTask.assignee?.name ?? 'Unassigned',
            newValue: taskData.assignedToUserName ?? 'Unknown',
    });
  }
  
  // Check for priority change
  if (taskData.priority && taskData.priority !== oldTask.priority) {
    changes.push({
      type: 'field-update',
      description: 'changed priority',
      oldValue: oldTask.priority,
      newValue: taskData.priority,
    });
  }
  
  // Build updated task
  const updatedTask: Task = {
    ...oldTask,
    ...taskData,
    // Preserve assignee structure if provided
    assignee: taskData.assignedToUserId ? {
      id: taskData.assignedToUserId,
      name: taskData.assignedToUserName ?? oldTask.assignee?.name ?? 'Unknown',
      email: taskData.assignedToUserEmail ?? oldTask.assignee?.email ?? '',
      avatar: taskData.assignedToUserAvatar || oldTask.assignee?.avatar,
      type: (taskData.assignedToUserType ?? oldTask.assignee?.type ?? 'user') as 'user' | 'group',
    } : oldTask.assignee,
    updatedAt: now,
    updatedBy: 'Current User',
    lastActivityAt: now,
    lastActivityBy: 'Current User',
  };

  // Add activity entries for changes
  changes.forEach(change => {
    demoActivity.push({
      id: `activity-${Date.now()}-${Math.random()}`,
      taskId: id,
      type: change.type as any,
      description: change.description,
      createdAt: now,
      createdBy: 'current-user',
      createdByName: 'Current User',
      oldValue: change.oldValue,
      newValue: change.newValue,
    });
  });

  mockTasks[taskIndex] = updatedTask;
  return updatedTask;
}

/**
 * Delete a task (demo mode)
 */
export function deleteTaskDemo(id: string): void {
  const taskIndex = mockTasks.findIndex(t => t.id === id);
  if (taskIndex === -1) {
    throw new Error(`Task with ID ${id} not found`);
  }

  mockTasks.splice(taskIndex, 1);
}

/**
 * Add a link to a task (demo mode)
 */
export function addLinkDemo(taskId: string, link: { linkType: string; linkedEntityId: string; linkedEntityName?: string; linkedEntityDescription?: string }): any {
  const taskIndex = mockTasks.findIndex(t => t.id === taskId);
  if (taskIndex === -1) {
    throw new Error(`Task with ID ${taskId} not found`);
  }

  const now = new Date().toISOString();
  const newLink = {
    id: `link-${Date.now()}`,
    assetId: link.linkedEntityId,
    assetName: link.linkedEntityName ?? 'Unknown Asset',
    assetCode: link.linkedEntityDescription,
    linkedAt: now,
    linkedBy: 'Current User',
  };

  if (!mockTasks[taskIndex].linkedAssets) {
    mockTasks[taskIndex].linkedAssets = [];
  }
  mockTasks[taskIndex].linkedAssets.push(newLink);

  // Add activity entry
  demoActivity.push({
    id: `activity-${Date.now()}-${Math.random()}`,
    taskId: taskId,
    type: 'link-added',
    description: 'linked asset',
    createdAt: now,
    createdBy: 'current-user',
    createdByName: 'Current User',
    newValue: link.linkedEntityName ?? 'Unknown Asset',
  });

  return newLink;
}

/**
 * Delete a link from a task (demo mode)
 */
export function deleteLinkDemo(taskId: string, linkId: string): void {
  const taskIndex = mockTasks.findIndex(t => t.id === taskId);
  if (taskIndex === -1) {
    throw new Error(`Task with ID ${taskId} not found`);
  }

  if (mockTasks[taskIndex].linkedAssets) {
    // Find the link being removed for activity log
    const removedLink = mockTasks[taskIndex].linkedAssets.find(l => l.id === linkId);
    
    // Remove the link
    mockTasks[taskIndex].linkedAssets = mockTasks[taskIndex].linkedAssets.filter(l => l.id !== linkId);
    
    // Add activity entry if link was found
    if (removedLink) {
      const now = new Date().toISOString();
      demoActivity.push({
        id: `activity-${Date.now()}-${Math.random()}`,
        taskId: taskId,
        type: 'link-removed',
        description: 'removed linked asset',
        createdAt: now,
        createdBy: 'current-user',
        createdByName: 'Current User',
        oldValue: removedLink.assetName ?? 'Unknown Asset',
      });
    }
  }
}
